import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ErrorTags, reportAppError } from "../../../errorReporter";
import useAssessmentController from "../../../hooks/useAssessmentController";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  invokeUserRefetch,
  setCurrentQuizProgress,
} from "../../../store/slices/storeSlice";
import { LessonDataI, QuestionData } from "../typesAndEnums";
import { handleSubmitQuizToServer } from "../utils/handleSubmitQuizToServer";

interface QuizData {
  score?: number;
  maxScore?: number;
  percentage?: number;
  isEnded: boolean;
  isStarted: boolean;
  isExpired: boolean;
  passed: boolean;
  remainingTime: number;
  startedTimestamp?: string | null;
  questions: QuestionData[];
}

const useQuizHandler = ({ lessonData }: { lessonData: LessonDataI }) => {
  const [quizData, setQuizData] = useState<QuizData>();
  const [isSubmitPending, setIsSubmitPending] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { getResult } = useAssessmentController();
  const { currentQuizProgress } = useAppSelector((state) => state.store);
  let startedTimestamp: string;

  const handleSubmitQuiz = async () => {
    // Safeguard: Ensure quiz has actually started before allowing submission
    const quizStartTimestamp = localStorage.getItem(
      `quiz_${lessonData?.quiz_id}_start_date`
    );

    if (!quizStartTimestamp) {
      console.error("Cannot submit quiz: Quiz has not been started yet");
      return;
    }

    const startTime = Number(quizStartTimestamp);
    const currentTime = Date.now();
    const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);

    // Safeguard: Ensure at least 2 seconds have passed since quiz start
    // This prevents premature submissions due to timing issues
    if (elapsedSeconds < 2) {
      console.error(
        `Cannot submit quiz: Only ${elapsedSeconds} seconds have elapsed. Minimum 2 seconds required.`
      );
      return;
    }

    console.log(`Submitting quiz after ${elapsedSeconds} seconds`);

    const result = await getResult({
      elapsedSeconds,
      userAnswers: currentQuizProgress,
      questions:
        JSON.parse(
          localStorage.getItem(`quiz_${lessonData?.quiz_id}_shuffled_questions`)
        ) ?? lessonData?.quiz_questions,
    });

    const resultSubmitted = await handleSubmitQuizToServer({
      lessonData,
      result,
      setIsSubmitPending,
      setSubmitError,
    });

    if (resultSubmitted) {
      dispatch(invokeUserRefetch());
      queryClient.invalidateQueries({
        queryKey: ["lesson"],
      });
      localStorage.removeItem(`quiz_${lessonData?.quiz_id}_start_date`);
      localStorage.removeItem(`quiz_${lessonData?.quiz_id}_user_answers`);
      localStorage.removeItem(`quiz_${lessonData?.quiz_id}_shuffled_questions`);
    }
  };

  const shuffleQuestions = (array: QuestionData[]) => {
    const previouslyShuffled = localStorage.getItem(
      `quiz_${lessonData?.quiz_id}_shuffled_questions`
    );

    if (previouslyShuffled) return JSON.parse(previouslyShuffled);

    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    localStorage.setItem(
      `quiz_${lessonData?.quiz_id}_shuffled_questions`,
      JSON.stringify(array)
    );
    return array;
  };

  const handleStartQuiz = () => {
    startedTimestamp = localStorage.getItem(
      `quiz_${lessonData?.quiz_id}_start_date`
    );

    if (startedTimestamp || !!lessonData?.past_quiz_trials) return;

    localStorage.setItem(
      `quiz_${lessonData?.quiz_id}_start_date`,
      `${Date.now()}`
    );
    setQuizData((prev) => ({ ...prev, isStarted: true }));
  };

  const calculateRemainigTime = () => {
    // Get the difference in milliseconds
    let differenceInMilliseconds = 0;

    if (startedTimestamp)
      differenceInMilliseconds = Math.abs(Date.now() - +startedTimestamp);

    // Convert the difference to seconds
    const differenceInSeconds = differenceInMilliseconds / 1000;

    return Math.ceil(lessonData?.quiz_duration - differenceInSeconds);
  };

  const getLastProgress = () => {
    const pastTrial = {};

    lessonData?.past_quiz_trials?.[
      lessonData?.past_quiz_trials?.length - 1
    ].answers[0]
      .split(",")
      .forEach((ans) => {
        const ansParts = ans.split("-");
        pastTrial[ansParts[0]] = ansParts[1];
      });

    const previousProgress =
      JSON.parse(
        localStorage.getItem(`quiz_${lessonData?.quiz_id}_user_answers`)
      ) ?? pastTrial;

    if (previousProgress) {
      dispatch(
        setCurrentQuizProgress({
          ...previousProgress,
        })
      );
    }
  };

  startedTimestamp = localStorage.getItem(
    `quiz_${lessonData?.quiz_id}_start_date`
  );
  const currentTimestamp = Date.now();
  const quizExpiryTimestamp = Date.parse(lessonData?.last_trial_date);

  const remainingTime = calculateRemainigTime();
  const quizExpired = lessonData?.last_trial_date
    ? currentTimestamp > quizExpiryTimestamp
    : false;
  let questionsData: QuestionData[] = useMemo(() => {
    if (lessonData?.past_quiz_trials) {
      const lastTrial =
        lessonData.past_quiz_trials[lessonData.past_quiz_trials.length - 1];
      const lastTrialRawData = JSON.parse(lastTrial.raw_data[0]);
      // remove score and maxScore
      delete lastTrialRawData?.score;
      delete lastTrialRawData?.maxScore;

      const lastShuffledQuestions = [];

      Object.entries(lastTrialRawData).forEach(
        ([key, value]: [string, any]) => {
          lastShuffledQuestions.push({
            question_id: key,
            order: value.order,
            userAnswer: value.answer,
            isCorrect: value.correct,
            ...lessonData?.quiz_questions.find((q) => q.question_id === +key),
          });
        }
      );
      return lastShuffledQuestions.sort((a, b) => a.order - b.order);
    } else if (lessonData?.quiz_randomize) {
      return shuffleQuestions(lessonData?.quiz_questions);
    } else {
      return lessonData?.quiz_questions;
    }
  }, [
    lessonData?.quiz_randomize,
    lessonData?.quiz_questions,
    lessonData?.quiz_id,
  ]);

  let maxScore = null;
  let score = null;
  let percentage = null;

  if (lessonData?.past_quiz_trials) {
    try {
      const qRes = JSON.parse(
        lessonData?.past_quiz_trials?.[lessonData?.past_quiz_trials.length - 1]
          ?.raw_data[0] ?? "{}"
      );

      maxScore = +qRes.maxScore;
      score = +qRes.score;
      percentage = (+qRes.score / +qRes.maxScore) * 100;
      questionsData = questionsData.map((q) => ({
        ...q,
        userAnswer: qRes[q.question_id]?.answer,
        isCorrect: qRes[q.question_id]?.correct,
      }));
    } catch (error) {
      reportAppError({
        e: error as Error,
        info: "error while parsing past quiz trial data",
        errorTag: ErrorTags.LessonScreen,
      });
    }
  }

  useEffect(() => {
    getLastProgress();
    handleStartQuiz();
    setQuizData({
      score,
      maxScore,
      percentage,
      passed: Number(lessonData?.quiz_pass_grade) <= Number(score),
      isEnded: !!lessonData?.past_quiz_trials,
      isStarted:
        !!localStorage.getItem(`quiz_${lessonData?.quiz_id}_start_date`) &&
        !quizExpired,
      isExpired: quizExpired && !lessonData?.past_quiz_trials,
      startedTimestamp,
      remainingTime,
      questions: questionsData,
    });
  }, [lessonData]);

  return {
    ...quizData,
    handleSubmitQuiz,
    isSubmitPending,
    submitError,
  };
};

export default useQuizHandler;
